import { FC, useRef, useState, DragEvent, ChangeEvent } from "react";
import {
  Box,
  Button,
  Typography,
  IconButton,
  Tooltip,
  CircularProgress,
  Stack
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CloseIcon from "@mui/icons-material/Close";
import { useQueryClient } from "@tanstack/react-query";
import { LifecycleAttachmentApi } from "../types";

type Props = {
  entityId: string;
  lifecycleName: string;
  attachmentApi: LifecycleAttachmentApi;
  onUploaded?: (message: string) => void;
};

const MAX_BYTES = 50 * 1024 * 1024; // 50 MB

type QueuedFile = {
  id: string;
  file: File;
};

const fileSignature = (file: File) =>
  `${file.name}-${file.size}-${file.lastModified}`;

const createQueuedFile = (file: File): QueuedFile => ({
  id: `${fileSignature(file)}-${crypto.randomUUID()}`,
  file
});

export const AttachmentUploader: FC<Props> = ({
  entityId,
  lifecycleName,
  attachmentApi,
  onUploaded
}) => {
  const [selectedFiles, setSelectedFiles] = useState<QueuedFile[]>([]);
  const [dragging, setDragging] = useState(false);
  const [sizeError, setSizeError] = useState<string | null>(null);
  const [uploadingFileId, setUploadingFileId] = useState<string | null>(null);
  const [uploadProgress, setUploadProgress] = useState({
    completed: 0,
    total: 0
  });
  const inputRef = useRef<HTMLInputElement>(null);

  const queryClient = useQueryClient();

  const upload = attachmentApi.useUpload();

  const addFiles = (files: FileList | null) => {
    if (!files?.length) return;

    const pickedFiles = Array.from(files);
    const validFiles = pickedFiles.filter((file) => file.size <= MAX_BYTES);
    const oversizedFiles = pickedFiles.filter((file) => file.size > MAX_BYTES);

    if (oversizedFiles.length > 0) {
      setSizeError(
        oversizedFiles.length === 1
          ? `${oversizedFiles[0].name} exceeds the 50 MB limit.`
          : `${oversizedFiles.length} files exceed the 50 MB limit.`
      );
    } else {
      setSizeError(null);
    }

    if (validFiles.length === 0) {
      return;
    }

    setSelectedFiles((currentFiles) => {
      const existingFiles = new Set(
        currentFiles.map(({ file }) => fileSignature(file))
      );
      const newFiles = validFiles.filter(
        (file) => !existingFiles.has(fileSignature(file))
      );

      return [...currentFiles, ...newFiles.map(createQueuedFile)];
    });
  };

  const removeFile = (fileId: string) => {
    setSelectedFiles((currentFiles) =>
      currentFiles.filter(({ id }) => id !== fileId)
    );
  };

  const handleDrop = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    addFiles(e.dataTransfer.files);
  };

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    addFiles(e.target.files);
    // reset so same file can be re-selected after clearing
    e.target.value = "";
  };

  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;

    const filesToUpload = selectedFiles;
    let uploadedCount = 0;
    let lastResult = "";

    setUploadProgress({ completed: 0, total: filesToUpload.length });

    try {
      for (const [index, queuedFile] of filesToUpload.entries()) {
        setUploadingFileId(queuedFile.id);
        setUploadProgress({ completed: index, total: filesToUpload.length });

        lastResult = await upload.mutateAsync({
          entityId,
          lifecycleName,
          file: queuedFile.file
        });

        uploadedCount += 1;
        removeFile(queuedFile.id);
      }

      onUploaded?.(
        filesToUpload.length === 1
          ? lastResult || "File uploaded"
          : `${filesToUpload.length} files uploaded`
      );
    } catch {
      // React Query exposes the error through upload.error below.
    } finally {
      setUploadingFileId(null);
      setUploadProgress({ completed: 0, total: 0 });

      if (uploadedCount > 0) {
        queryClient.invalidateQueries({
          queryKey: attachmentApi.listKey(entityId, lifecycleName)
        });
      }
    }
  };

  const formatBytes = (bytes: number) =>
    bytes < 1024 * 1024
      ? `${(bytes / 1024).toFixed(0)} KB`
      : `${(bytes / (1024 * 1024)).toFixed(1)} MB`;

  const isUploading = uploadProgress.total > 0;
  const uploadLabel = isUploading
    ? `Uploading ${uploadProgress.completed + 1}/${uploadProgress.total}`
    : selectedFiles.length === 1
      ? "Upload file"
      : `Upload ${selectedFiles.length} files`;

  return (
    <Box sx={{ mb: 3 }}>
      <Box
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={() => setDragging(false)}
        onClick={() => inputRef.current?.click()}
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          border: "2px dashed",
          borderColor: (t) =>
            dragging
              ? t.palette.primary.main
              : t.palette.mode === "dark"
                ? t.palette.grey[600]
                : t.palette.grey[300],
          borderRadius: 1,
          p: 4,
          mb: 2,
          textAlign: "center",
          bgcolor: (t) =>
            dragging
              ? t.palette.action.hover
              : t.palette.mode === "dark"
                ? "#000"
                : t.palette.grey[50],
          cursor: isUploading ? "default" : "pointer",
          pointerEvents: isUploading ? "none" : "auto",
          transition: "border-color .2s, background .2s",
          "&:hover": {
            borderColor: "primary.main",
            bgcolor: (t) => t.palette.action.hover
          }
        }}
      >
        <UploadFileIcon sx={{ fontSize: 36, color: "text.disabled", mb: 1 }} />
        <Typography
          variant="body2"
          fontWeight={600}
          color="text.secondary"
          gutterBottom
        >
          Drag &amp; drop files here
        </Typography>
        <Typography
          variant="caption"
          color="text.disabled"
          display="block"
          mb={1.5}
        >
          PDF, Word, Excel, images - max 50 MB each
        </Typography>
        <Button
          variant="text"
          color="warning"
          size="small"
          sx={{ pointerEvents: "none" }}
        >
          Select files
        </Button>
        <input
          ref={inputRef}
          hidden
          multiple
          type="file"
          onChange={handleInputChange}
        />
      </Box>

      {selectedFiles.length > 0 && (
        <Box
          sx={{
            px: 2,
            py: 1.5,
            mb: 2,
            border: "1px solid",
            borderColor: "primary.light",
            borderRadius: 1,
            bgcolor: "primary.50"
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
              mb: 1.5
            }}
          >
            <Typography variant="body2" fontWeight={700}>
              Selected files ({selectedFiles.length})
            </Typography>
            <Box sx={{ display: "flex", gap: 1 }}>
              <Button
                variant="contained"
                size="small"
                disableElevation
                onClick={handleUpload}
                disabled={isUploading}
                startIcon={
                  isUploading ? (
                    <CircularProgress size={12} color="inherit" />
                  ) : undefined
                }
                sx={{ flexShrink: 0 }}
              >
                {uploadLabel}
              </Button>
              <Tooltip title="Clear selected files">
                <span>
                  <IconButton
                    size="small"
                    onClick={() => setSelectedFiles([])}
                    disabled={isUploading}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </Box>
          </Box>

          <Stack spacing={1}>
            {selectedFiles.map(({ id, file }) => {
              const isCurrentUpload = uploadingFileId === id;

              return (
                <Box
                  key={id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    px: 1.5,
                    py: 1,
                    border: "1px solid",
                    borderColor: isCurrentUpload ? "primary.main" : "divider",
                    borderRadius: 1,
                    bgcolor: "background.paper"
                  }}
                >
                  {isCurrentUpload ? (
                    <CircularProgress size={18} />
                  ) : (
                    <UploadFileIcon fontSize="small" color="primary" />
                  )}
                  <Typography
                    variant="body2"
                    fontWeight={500}
                    sx={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap"
                    }}
                    title={file.name}
                  >
                    {file.name}
                  </Typography>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    sx={{ flexShrink: 0 }}
                  >
                    {formatBytes(file.size)}
                  </Typography>
                  <Tooltip title="Remove">
                    <span>
                      <IconButton
                        size="small"
                        onClick={() => removeFile(id)}
                        disabled={isUploading}
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              );
            })}
          </Stack>
        </Box>
      )}

      {sizeError && (
        <Typography variant="caption" color="error">
          {sizeError}
        </Typography>
      )}
      {upload.error && (
        <Typography
          variant="caption"
          color="error"
          sx={{ display: "block", mt: sizeError ? 0.5 : 0 }}
        >
          {upload.error.message}
        </Typography>
      )}
    </Box>
  );
};
